import { SERVICES_URLS } from "@sajilni/config";
import {
  CommunicationLogStatus,
  NotificationEntities,
  NotificationsType,
  ParticipantType,
  Provider,
  prisma,
  TemplateV2Type,
  type CommunicationSettingsStatus,
} from "client";
import { sendExpoNotification } from "@sajilni/utils/expoNotification";
import { createIdempotentCommunicationLog } from "../communicationLog";
import {
  rollupFiniteCommunicationStatus,
  updateCommunicationSettingsStatus,
} from "../communicationSettings";
import { getDeviceTokensByUser } from "../deviceToken";
import { createNotification } from "../notification";
import { resolvePushNotificationMergeTags } from "./notificationMergeTags.pure";
import { classifyNotificationPreflight } from "./notificationPreflight.pure";

export type ProcessCommunicationSendResult =
  | { success: true }
  | { success: false; error: string };

export type CommunicationOccurrence = {
  recipientProfileId?: string;
  occurrenceKey?: string;
};

const communicationSendSelect = {
  id: true,
  status: true,
  templateV2Id: true,
  audienceProfileIds: true,
  templateV2: {
    select: {
      id: true,
      type: true,
      scopeId: true,
      config: true,
    },
  },
  triggerSession: {
    select: {
      translations: {
        select: {
          name: true,
          location: true,
        },
        take: 1,
      },
    },
  },
} as const;

function idempotencyKey(
  settingsId: string,
  profileId: string,
  occurrenceKey: string,
  provider: string
) {
  return `${settingsId}/${profileId}/${occurrenceKey}/${provider}`;
}

async function writeDualProviderFailureLogs(input: {
  email?: string | null;
  phoneNumber?: string | null;
  templateV2Id: string;
  communicationSettingsId: string;
  recipientProfileId: string;
  occurrenceKey: string;
  failedReason: string;
  payload?: { title: string; body: string };
}) {
  await createIdempotentCommunicationLog({
    email: input.email,
    phoneNumber: input.phoneNumber,
    templateV2Id: input.templateV2Id,
    communicationSettingsId: input.communicationSettingsId,
    recipientProfileId: input.recipientProfileId,
    occurrenceKey: input.occurrenceKey,
    provider: Provider.IN_APP,
    providerMessageId: `failed-${input.occurrenceKey}`,
    status: CommunicationLogStatus.FAILED,
    failedReason: input.failedReason,
    idempotencyKey: idempotencyKey(
      input.communicationSettingsId,
      input.recipientProfileId,
      input.occurrenceKey,
      "IN_APP"
    ),
    payload: input.payload,
  });
  await createIdempotentCommunicationLog({
    email: input.email,
    phoneNumber: input.phoneNumber,
    templateV2Id: input.templateV2Id,
    communicationSettingsId: input.communicationSettingsId,
    recipientProfileId: input.recipientProfileId,
    occurrenceKey: input.occurrenceKey,
    provider: Provider.EXPO,
    providerMessageId: `failed-${input.occurrenceKey}`,
    status: CommunicationLogStatus.FAILED,
    failedReason: input.failedReason,
    idempotencyKey: idempotencyKey(
      input.communicationSettingsId,
      input.recipientProfileId,
      input.occurrenceKey,
      "EXPO"
    ),
    payload: input.payload,
  });
}

async function sendNotificationOccurrence(
  communication: {
    id: string;
    templateV2Id: string;
    templateV2: {
      id: string;
      scopeId: string | null;
      config: { notification?: { title: string; body: string } | null } | null;
    };
    triggerSession: {
      translations: Array<{ name: string; location: string | null }>;
    } | null;
  },
  occurrence: { recipientProfileId: string; occurrenceKey: string }
): Promise<ProcessCommunicationSendResult> {
  const eventId = communication.templateV2.scopeId;
  const templateTitle =
    communication.templateV2.config?.notification?.title ?? "";
  const templateBody =
    communication.templateV2.config?.notification?.body ?? "";
  if (
    classifyNotificationPreflight({
      eventId,
      title: templateTitle,
      body: templateBody,
    }).action === "fail-campaign"
  ) {
    await writeDualProviderFailureLogs({
      templateV2Id: communication.templateV2Id,
      communicationSettingsId: communication.id,
      recipientProfileId: occurrence.recipientProfileId,
      occurrenceKey: occurrence.occurrenceKey,
      failedReason: "Missing notification content or eventId",
    });
    await rollupFiniteCommunicationStatus(communication.id);
    return { success: true };
  }
  if (!eventId) {
    return { success: true };
  }

  const profile = await prisma.profile.findUnique({
    where: { id: occurrence.recipientProfileId },
    select: {
      id: true,
      email: true,
      name: true,
      phoneNumber: true,
      userId: true,
    },
  });
  if (!profile) {
    await writeDualProviderFailureLogs({
      templateV2Id: communication.templateV2Id,
      communicationSettingsId: communication.id,
      recipientProfileId: occurrence.recipientProfileId,
      occurrenceKey: occurrence.occurrenceKey,
      failedReason: "Recipient profile not found",
    });
    await rollupFiniteCommunicationStatus(communication.id);
    return { success: true };
  }

  const baseLog = {
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    templateV2Id: communication.templateV2Id,
    communicationSettingsId: communication.id,
    recipientProfileId: profile.id,
    occurrenceKey: occurrence.occurrenceKey,
  };

  const event = await prisma.event.findUnique({
    where: { id: eventId },
    select: {
      whiteLabelId: true,
      translations: { select: { name: true }, take: 1 },
    },
  });
  const { title, body } = resolvePushNotificationMergeTags(
    { title: templateTitle, body: templateBody },
    {
      recipientName: profile.name,
      sessionName: communication.triggerSession?.translations[0]?.name,
      sessionLocation: communication.triggerSession?.translations[0]?.location,
      eventName: event?.translations[0]?.name,
    }
  );
  const notificationPayload = { title, body };
  const loginEmail = profile.email?.trim().toLowerCase();
  const loginUser =
    loginEmail && event?.whiteLabelId
      ? await prisma.user.findUnique({
          where: {
            email_accessibleWhiteLabelId: {
              email: loginEmail,
              accessibleWhiteLabelId: event.whiteLabelId,
            },
          },
          select: { id: true },
        })
      : null;
  const receiverUserId = loginUser?.id ?? profile.userId;

  if (!receiverUserId) {
    await writeDualProviderFailureLogs({
      ...baseLog,
      failedReason: "Profile is not linked to a user",
      payload: notificationPayload,
    });
    await rollupFiniteCommunicationStatus(communication.id);
    return { success: true };
  }

  const inAppKey = idempotencyKey(
    communication.id,
    profile.id,
    occurrence.occurrenceKey,
    "IN_APP"
  );
  try {
    const notification = await createNotification({
      type: NotificationsType.COMMUNICATION,
      entity: NotificationEntities.COMMUNICATION,
      entityId: communication.id,
      receiverId: receiverUserId,
      sender: ParticipantType.USER,
      receiver: ParticipantType.USER,
      eventId,
      title,
      body,
    });
    await createIdempotentCommunicationLog({
      ...baseLog,
      provider: Provider.IN_APP,
      providerMessageId: notification.id,
      status: CommunicationLogStatus.DELIVERED,
      idempotencyKey: inAppKey,
      payload: notificationPayload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    await createIdempotentCommunicationLog({
      ...baseLog,
      provider: Provider.IN_APP,
      providerMessageId: `in-app-error-${occurrence.occurrenceKey}`,
      status: CommunicationLogStatus.FAILED,
      failedReason: message,
      idempotencyKey: inAppKey,
      payload: notificationPayload,
    });
  }

  const expoKey = idempotencyKey(
    communication.id,
    profile.id,
    occurrence.occurrenceKey,
    "EXPO"
  );
  try {
    const tokens = await getDeviceTokensByUser(receiverUserId, eventId);
    if (tokens.length === 0) {
      await createIdempotentCommunicationLog({
        ...baseLog,
        provider: Provider.EXPO,
        providerMessageId: `no-token-${occurrence.occurrenceKey}`,
        status: CommunicationLogStatus.FAILED,
        failedReason: "No device tokens for this event",
        idempotencyKey: expoKey,
        payload: notificationPayload,
      });
    } else {
      const expo = await sendExpoNotification(
        receiverUserId,
        tokens,
        title,
        {
          type: "COMMUNICATION",
          communicationSettingsId: communication.id,
          occurrenceKey: occurrence.occurrenceKey,
        },
        body
      );
      const accepted = expo.tickets.find((ticket) => ticket.status === "ok");
      await createIdempotentCommunicationLog({
        ...baseLog,
        provider: Provider.EXPO,
        providerMessageId: accepted?.id || `expo-${occurrence.occurrenceKey}`,
        status: accepted
          ? CommunicationLogStatus.SENT
          : CommunicationLogStatus.FAILED,
        failedReason: accepted ? null : expo.error,
        idempotencyKey: expoKey,
        payload: { ...notificationPayload, tickets: expo.tickets },
      });
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    await createIdempotentCommunicationLog({
      ...baseLog,
      provider: Provider.EXPO,
      providerMessageId: `expo-error-${occurrence.occurrenceKey}`,
      status: CommunicationLogStatus.FAILED,
      failedReason: message,
      idempotencyKey: expoKey,
      payload: notificationPayload,
    });
  }

  await rollupFiniteCommunicationStatus(communication.id);
  return { success: true };
}

/**
 * Sends a communication via the notification app's TemplateV2 endpoint and
 * updates CommunicationSettings status (SENT / FAILED).
 */
export async function processCommunicationSettingsSend(
  communicationSettingsId: string,
  occurrence?: CommunicationOccurrence
): Promise<ProcessCommunicationSendResult> {
  const communication = await prisma.communicationSettings.findUnique({
    where: { id: communicationSettingsId },
    select: communicationSendSelect,
  });

  if (!communication) {
    return { success: false, error: "Communication settings not found" };
  }

  if (communication.status === "SENT" && !occurrence?.recipientProfileId) {
    return { success: true };
  }

  if (!communication.templateV2Id || !communication.templateV2) {
    await updateCommunicationSettingsStatus(communicationSettingsId, "FAILED");
    return { success: false, error: "Missing TemplateV2" };
  }

  if (communication.templateV2.type === TemplateV2Type.NOTIFICATION) {
    if (!occurrence?.recipientProfileId) {
      return {
        success: false,
        error: "NOTIFICATION sends require recipientProfileId",
      };
    }
    return sendNotificationOccurrence(
      {
        id: communication.id,
        templateV2Id: communication.templateV2Id,
        templateV2: communication.templateV2,
        triggerSession: communication.triggerSession,
      },
      {
        recipientProfileId: occurrence.recipientProfileId,
        occurrenceKey: occurrence.occurrenceKey ?? "default",
      }
    );
  }

  const eventId = communication.templateV2.scopeId;
  if (!eventId) {
    await updateCommunicationSettingsStatus(communicationSettingsId, "FAILED");
    return { success: false, error: "Missing eventId on template" };
  }

  const audienceProfiles = await prisma.profile.findMany({
    where: { id: { in: communication.audienceProfileIds } },
    select: {
      id: true,
      email: true,
      name: true,
      phoneNumber: true,
    },
  });
  const users = audienceProfiles
    .filter((profile) => profile.email)
    .map((profile) => ({
      email: profile.email!,
      name: profile.name || undefined,
      phoneNumber: profile.phoneNumber || undefined,
      profileId: profile.id,
    }));

  if (users.length === 0) {
    await updateCommunicationSettingsStatus(communicationSettingsId, "FAILED");
    return { success: false, error: "No valid users with email" };
  }

  if (communication.templateV2.type !== TemplateV2Type.EMAIL) {
    await updateCommunicationSettingsStatus(communicationSettingsId, "FAILED");
    return {
      success: false,
      error: `TemplateV2 type "${communication.templateV2.type}" is not supported yet`,
    };
  }

  const sendUrl = `${SERVICES_URLS.NOTIFICATION_APP}/api/templateV2/${communication.templateV2Id}/send`;

  if (communication.status === "SCHEDULED") {
    await updateCommunicationSettingsStatus(
      communicationSettingsId,
      "PROCESSING"
    );
  }

  try {
    const response = await fetch(sendUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        users,
        eventId,
        communicationSettingsId: communication.id,
      }),
    });

    if (!response.ok) {
      const details = await response.text();
      await updateCommunicationSettingsStatus(
        communicationSettingsId,
        "FAILED"
      );
      return {
        success: false,
        error:
          details ||
          `Failed to send TemplateV2 communication (${response.status})`,
      };
    }

    await updateCommunicationSettingsStatus(communicationSettingsId, "SENT");
    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    await updateCommunicationSettingsStatus(communicationSettingsId, "FAILED");
    return { success: false, error: message };
  }
}

export async function markCommunicationSettingsProcessing(
  communicationSettingsId: string,
  allowedStatuses: CommunicationSettingsStatus[] = ["SCHEDULED"]
) {
  const updated = await prisma.communicationSettings.updateMany({
    where: {
      id: communicationSettingsId,
      status: { in: allowedStatuses },
    },
    data: { status: "PROCESSING" },
  });
  return updated.count > 0;
}
